/**
 * Hybrid Event Store
 *
 * Zustand store for managing hybrid events in the Vibe Remote web UI:
 * - PTY stream frames (terminal output)
 * - Visual events (tool lifecycle, agent lifecycle, progress)
 * - Session state transitions
 *
 * This store is designed to work alongside the existing happyStore,
 * providing dedicated state management for the new hybrid event system.
 *
 * Phase: Hybrid Orchestrator Migration
 * Project: web-ui-dual-rendering
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  SessionState,
  StreamFrame,
  StreamChunk,
  VisualToolStart,
  VisualToolEnd,
  VisualAgentSpawn,
  VisualAgentUpdate,
  VisualAgentComplete,
  VisualPermissionRequest,
  VisualProgress,
  AttachmentStatusData,
  ClientChunkBuffer,
} from '../types/hybridEvents';
import { ClientChunkBuffer as ChunkBufferImpl } from '../types/hybridEvents';

// ============================================
// Types
// ============================================

/**
 * PTY frame entry with metadata
 */
export interface PtyFrame {
  id: string;
  sessionId: string;
  timestamp: number;
  seq?: number;
  content: string;
  frameType: 'assistant' | 'system' | 'error' | 'debug';
  isFinal: boolean;
  tokenCount?: number;
}

/**
 * Active tool state
 */
export interface ActiveTool {
  toolId: string;
  toolName: string;
  operation?: string;
  resource?: string;
  preview?: string;
  startTime: number;
  seq?: number;
}

/**
 * Completed tool state
 */
export interface CompletedTool extends ActiveTool {
  endTime: number;
  durationMs: number;
  success: boolean;
  result?: string;
  error?: string;
}

/**
 * Active agent state
 */
export interface ActiveAgent {
  agentId: string;
  agentType: string;
  description?: string;
  parentAgentId?: string;
  spawnTime: number;
  status?: string;
  progress?: number;
  currentTask?: string;
  seq?: number;
}

/**
 * Completed agent state
 */
export interface CompletedAgent extends ActiveAgent {
  endTime: number;
  durationMs: number;
  success: boolean;
  result?: string;
  error?: string;
}

/**
 * Permission request state
 */
export interface PermissionRequest {
  permissionId: string;
  toolId?: string;
  toolName?: string;
  permissionType: string;
  resource: string;
  description?: string;
  preview?: string;
  riskLevel?: string;
  timeoutMs?: number;
  remainingMs?: number;
  requestTime: number;
  seq?: number;
}

/**
 * Progress indicator state
 */
export interface ProgressState {
  progressId?: string;
  current: number;
  total: number;
  message?: string;
  phase?: string;
  timestamp: number;
  seq?: number;
}

/**
 * Session hybrid state
 */
export interface SessionHybridState {
  // State
  currentState: SessionState;
  previousState?: SessionState;
  stateHistory: Array<{ state: SessionState; timestamp: number; reason?: string }>;

  // PTY frames
  frames: PtyFrame[];
  accumulatedContent: string;
  lastFrameSeq?: number;

  // Tools
  activeTools: Map<string, ActiveTool>;
  completedTools: CompletedTool[];

  // Agents
  activeAgents: Map<string, ActiveAgent>;
  completedAgents: CompletedAgent[];

  // Permissions
  pendingPermissions: Map<string, PermissionRequest>;
  respondedPermissions: string[];

  // Progress
  activeProgress: ProgressState | null;

  // Attachments
  attachmentStatuses: Map<string, AttachmentStatusData>;

  // Chunk buffer for stream:chunk reassembly
  chunkBuffer: ClientChunkBuffer | null;
}

/**
 * User preference for rendering mode
 */
export type RenderingMode = 'auto' | 'terminal' | 'structured' | 'dual';

/**
 * Hybrid store state
 */
export interface HybridStoreState {
  // Session states
  sessions: Map<string, SessionHybridState>;

  // User preferences
  renderingMode: RenderingMode;
  terminalPaneVisible: boolean;
  structuredPanelVisible: boolean;
  splitRatio: number; // 0.0-1.0, ratio of terminal pane width

  // Configuration
  maxFramesPerSession: number;
  maxStateHistory: number;
  maxCompletedTools: number;
  maxCompletedAgents: number;
}

/**
 * Hybrid store actions
 */
export interface HybridStoreActions {
  // Session management
  initSession: (sessionId: string) => void;
  clearSession: (sessionId: string) => void;

  // State events
  handleStateUpdate: (event: {
    sessionId: string;
    state: SessionState;
    previousState?: string;
    reason?: string;
    timestamp: number;
    seq?: number;
    metadata?: Record<string, unknown>;
  }) => void;

  // Stream events
  handleStreamFrame: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    content: string;
    frameType: 'assistant' | 'system' | 'error' | 'debug';
    isFinal: boolean;
    tokenCount?: number;
  }) => void;
  handleStreamChunk: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    content: string;
    chunkIndex: number;
    totalChunks?: number;
    isFinal: boolean;
  }) => void;

  // Visual events - Tools
  handleVisualToolStart: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    toolId: string;
    toolName: string;
    operation?: string;
    resource?: string;
    preview?: string;
  }) => void;
  handleVisualToolEnd: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    toolId: string;
    success: boolean;
    result?: string;
    error?: string;
    durationMs?: number;
  }) => void;

  // Visual events - Agents
  handleVisualAgentSpawn: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    agentId: string;
    agentType: string;
    description?: string;
    parentAgentId?: string;
  }) => void;
  handleVisualAgentUpdate: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    agentId: string;
    status?: string;
    progress?: number;
    currentTask?: string;
  }) => void;
  handleVisualAgentComplete: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    agentId: string;
    success: boolean;
    result?: string;
    error?: string;
    durationMs?: number;
  }) => void;

  // Visual events - Permissions
  handleVisualPermissionRequest: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    permissionId: string;
    toolId?: string;
    toolName?: string;
    permissionType: string;
    resource: string;
    description?: string;
    preview?: string;
    riskLevel?: string;
    timeoutMs?: number;
    remainingMs?: number;
  }) => void;
  markPermissionResponded: (sessionId: string, permissionId: string) => void;

  // Visual events - Progress
  handleVisualProgress: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    progressId?: string;
    current: number;
    total: number;
    message?: string;
    phase?: string;
  }) => void;
  clearProgress: (sessionId: string) => void;

  // Visual events - Attachments
  handleVisualAttachmentStatus: (event: {
    sessionId: string;
    timestamp: number;
    seq?: number;
    status: {
      attachmentId: string;
      name: string;
      status: 'uploading' | 'processing' | 'ready' | 'failed';
      progress?: number;
      error?: string;
    };
  }) => void;

  // User preferences
  setRenderingMode: (mode: RenderingMode) => void;
  setTerminalPaneVisible: (visible: boolean) => void;
  setStructuredPanelVisible: (visible: boolean) => void;
  setSplitRatio: (ratio: number) => void;

  // Getters
  getSession: (sessionId: string) => SessionHybridState | undefined;
  getActiveToolCount: (sessionId: string) => number;
  getActiveAgentCount: (sessionId: string) => number;
  getPendingPermissionCount: (sessionId: string) => number;
  getSessionState: (sessionId: string) => SessionState | undefined;
  getTerminalContent: (sessionId: string) => string;
}

// ============================================
// Initial State
// ============================================

const createInitialSessionState = (): SessionHybridState => ({
  currentState: 'idle',
  previousState: undefined,
  stateHistory: [],
  frames: [],
  accumulatedContent: '',
  lastFrameSeq: undefined,
  activeTools: new Map(),
  completedTools: [],
  activeAgents: new Map(),
  completedAgents: [],
  pendingPermissions: new Map(),
  respondedPermissions: [],
  activeProgress: null,
  attachmentStatuses: new Map(),
  chunkBuffer: null,
});

const initialState: HybridStoreState = {
  sessions: new Map(),
  renderingMode: 'auto',
  terminalPaneVisible: true,
  structuredPanelVisible: true,
  splitRatio: 0.5,
  maxFramesPerSession: 1000,
  maxStateHistory: 50,
  maxCompletedTools: 100,
  maxCompletedAgents: 50,
};

// ============================================
// Store
// ============================================

export const useHybridStore = create<HybridStoreState & HybridStoreActions>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      // ============================================
      // Session Management
      // ============================================

      initSession: (sessionId: string) => {
        set((state) => {
          if (state.sessions.has(sessionId)) {
            return state; // Already initialized
          }
          const newSessions = new Map(state.sessions);
          newSessions.set(sessionId, createInitialSessionState());
          return { sessions: newSessions };
        });
      },

      clearSession: (sessionId: string) => {
        set((state) => {
          const newSessions = new Map(state.sessions);
          newSessions.delete(sessionId);
          return { sessions: newSessions };
        });
      },

      // ============================================
      // State Events
      // ============================================

      handleStateUpdate: (event) => {
        const { sessionId, state, previousState, reason, timestamp, seq } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          // Update state
          const newHistory = [
            ...session.stateHistory,
            { state, timestamp, reason },
          ];

          // Trim history
          if (newHistory.length > storeState.maxStateHistory) {
            newHistory.shift();
          }

          newSessions.set(sessionId, {
            ...session,
            currentState: state,
            previousState: (previousState as SessionState) || session.currentState,
            stateHistory: newHistory,
          });

          return { sessions: newSessions };
        });

        console.log('[HybridStore] State update:', sessionId, state);
      },

      // ============================================
      // Stream Events
      // ============================================

      handleStreamFrame: (event) => {
        const { sessionId, timestamp, seq, content, frameType, isFinal, tokenCount } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          // Create frame
          const frame: PtyFrame = {
            id: `frame-${timestamp}-${seq || 0}`,
            sessionId,
            timestamp,
            seq,
            content,
            frameType,
            isFinal,
            tokenCount,
          };

          // Check for sequence gaps
          if (seq !== undefined && session.lastFrameSeq !== undefined) {
            if (seq !== session.lastFrameSeq + 1) {
              console.warn('[HybridStore] Frame sequence gap detected:',
                session.lastFrameSeq, '->', seq);
            }
          }

          // Add frame and accumulate content
          const newFrames = [...session.frames, frame];
          if (newFrames.length > storeState.maxFramesPerSession) {
            newFrames.shift();
          }

          const newAccumulated = session.accumulatedContent + content;

          newSessions.set(sessionId, {
            ...session,
            frames: newFrames,
            accumulatedContent: newAccumulated,
            lastFrameSeq: seq,
          });

          return { sessions: newSessions };
        });
      },

      handleStreamChunk: (event) => {
        const { sessionId, timestamp, seq, content, chunkIndex, totalChunks, isFinal } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          // Initialize chunk buffer if needed
          let buffer = session.chunkBuffer;
          if (!buffer || chunkIndex === 0) {
            buffer = new ChunkBufferImpl(sessionId);
          }

          // Add chunk
          const chunk: StreamChunk = {
            eventType: 'stream:chunk',
            sessionId,
            timestamp,
            seq,
            content,
            chunkIndex,
            totalChunks,
            isFinal,
          };

          const isComplete = buffer.addChunk(chunk);

          if (isComplete) {
            // Reassemble and add as frame
            const reassembled = buffer.assemble();
            const frame: PtyFrame = {
              id: `chunk-reassembled-${timestamp}`,
              sessionId,
              timestamp,
              seq,
              content: reassembled,
              frameType: 'assistant',
              isFinal: true,
            };

            const newFrames = [...session.frames, frame];
            if (newFrames.length > storeState.maxFramesPerSession) {
              newFrames.shift();
            }

            const newAccumulated = session.accumulatedContent + reassembled;

            newSessions.set(sessionId, {
              ...session,
              frames: newFrames,
              accumulatedContent: newAccumulated,
              chunkBuffer: null, // Clear buffer
            });
          } else {
            newSessions.set(sessionId, {
              ...session,
              chunkBuffer: buffer,
            });
          }

          return { sessions: newSessions };
        });
      },

      // ============================================
      // Visual Events - Tools
      // ============================================

      handleVisualToolStart: (event) => {
        const { sessionId, timestamp, seq, toolId, toolName, operation, resource, preview } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          const newActiveTools = new Map(session.activeTools);
          newActiveTools.set(toolId, {
            toolId,
            toolName,
            operation,
            resource,
            preview,
            startTime: timestamp * 1000,
            seq,
          });

          newSessions.set(sessionId, {
            ...session,
            activeTools: newActiveTools,
          });

          return { sessions: newSessions };
        });

        console.log('[HybridStore] Tool started:', toolName, toolId);
      },

      handleVisualToolEnd: (event) => {
        const { sessionId, timestamp, seq, toolId, success, result, error, durationMs } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            return storeState;
          }

          const activeTool = session.activeTools.get(toolId);
          if (!activeTool) {
            console.warn('[HybridStore] Tool end for unknown tool:', toolId);
            return storeState;
          }

          // Move to completed
          const newActiveTools = new Map(session.activeTools);
          newActiveTools.delete(toolId);

          const completedTool: CompletedTool = {
            ...activeTool,
            endTime: timestamp * 1000,
            durationMs: durationMs || (timestamp * 1000 - activeTool.startTime),
            success,
            result,
            error,
          };

          const newCompleted = [...session.completedTools, completedTool];
          if (newCompleted.length > storeState.maxCompletedTools) {
            newCompleted.shift();
          }

          newSessions.set(sessionId, {
            ...session,
            activeTools: newActiveTools,
            completedTools: newCompleted,
          });

          return { sessions: newSessions };
        });

        console.log('[HybridStore] Tool ended:', toolId, success ? 'success' : 'failed');
      },

      // ============================================
      // Visual Events - Agents
      // ============================================

      handleVisualAgentSpawn: (event) => {
        const { sessionId, timestamp, seq, agentId, agentType, description, parentAgentId } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          const newActiveAgents = new Map(session.activeAgents);
          newActiveAgents.set(agentId, {
            agentId,
            agentType,
            description,
            parentAgentId,
            spawnTime: timestamp * 1000,
            seq,
          });

          newSessions.set(sessionId, {
            ...session,
            activeAgents: newActiveAgents,
          });

          return { sessions: newSessions };
        });

        console.log('[HybridStore] Agent spawned:', agentType, agentId);
      },

      handleVisualAgentUpdate: (event) => {
        const { sessionId, timestamp, seq, agentId, status, progress, currentTask } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          const session = newSessions.get(sessionId);

          if (!session) {
            return storeState;
          }

          const activeAgent = session.activeAgents.get(agentId);
          if (!activeAgent) {
            console.warn('[HybridStore] Agent update for unknown agent:', agentId);
            return storeState;
          }

          const newActiveAgents = new Map(session.activeAgents);
          newActiveAgents.set(agentId, {
            ...activeAgent,
            status,
            progress,
            currentTask,
            seq,
          });

          newSessions.set(sessionId, {
            ...session,
            activeAgents: newActiveAgents,
          });

          return { sessions: newSessions };
        });
      },

      handleVisualAgentComplete: (event) => {
        const { sessionId, timestamp, seq, agentId, success, result, error, durationMs } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            return storeState;
          }

          const activeAgent = session.activeAgents.get(agentId);
          if (!activeAgent) {
            console.warn('[HybridStore] Agent complete for unknown agent:', agentId);
            return storeState;
          }

          // Move to completed
          const newActiveAgents = new Map(session.activeAgents);
          newActiveAgents.delete(agentId);

          const completedAgent: CompletedAgent = {
            ...activeAgent,
            endTime: timestamp * 1000,
            durationMs: durationMs || (timestamp * 1000 - activeAgent.spawnTime),
            success,
            result,
            error,
          };

          const newCompleted = [...session.completedAgents, completedAgent];
          if (newCompleted.length > storeState.maxCompletedAgents) {
            newCompleted.shift();
          }

          newSessions.set(sessionId, {
            ...session,
            activeAgents: newActiveAgents,
            completedAgents: newCompleted,
          });

          return { sessions: newSessions };
        });

        console.log('[HybridStore] Agent completed:', agentId, success ? 'success' : 'failed');
      },

      // ============================================
      // Visual Events - Permissions
      // ============================================

      handleVisualPermissionRequest: (event) => {
        const {
          sessionId, timestamp, seq, permissionId, toolId, toolName,
          permissionType, resource, description, preview, riskLevel,
          timeoutMs, remainingMs,
        } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          const newPending = new Map(session.pendingPermissions);
          newPending.set(permissionId, {
            permissionId,
            toolId,
            toolName,
            permissionType,
            resource,
            description,
            preview,
            riskLevel,
            timeoutMs,
            remainingMs,
            requestTime: timestamp * 1000,
            seq,
          });

          newSessions.set(sessionId, {
            ...session,
            pendingPermissions: newPending,
          });

          return { sessions: newSessions };
        });

        console.log('[HybridStore] Permission request:', permissionId, permissionType);
      },

      markPermissionResponded: (sessionId: string, permissionId: string) => {
        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          const session = newSessions.get(sessionId);

          if (!session) {
            return storeState;
          }

          const newPending = new Map(session.pendingPermissions);
          newPending.delete(permissionId);

          const newResponded = [...session.respondedPermissions, permissionId];

          newSessions.set(sessionId, {
            ...session,
            pendingPermissions: newPending,
            respondedPermissions: newResponded,
          });

          return { sessions: newSessions };
        });
      },

      // ============================================
      // Visual Events - Progress
      // ============================================

      handleVisualProgress: (event) => {
        const { sessionId, timestamp, seq, progressId, current, total, message, phase } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          newSessions.set(sessionId, {
            ...session,
            activeProgress: {
              progressId,
              current,
              total,
              message,
              phase,
              timestamp,
              seq,
            },
          });

          return { sessions: newSessions };
        });
      },

      clearProgress: (sessionId: string) => {
        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          const session = newSessions.get(sessionId);

          if (!session) {
            return storeState;
          }

          newSessions.set(sessionId, {
            ...session,
            activeProgress: null,
          });

          return { sessions: newSessions };
        });
      },

      // ============================================
      // Visual Events - Attachments
      // ============================================

      handleVisualAttachmentStatus: (event) => {
        const { sessionId, timestamp, seq, status } = event;

        set((storeState) => {
          const newSessions = new Map(storeState.sessions);
          let session = newSessions.get(sessionId);

          if (!session) {
            session = createInitialSessionState();
          }

          const newStatuses = new Map(session.attachmentStatuses);
          newStatuses.set(status.attachmentId, {
            id: status.attachmentId,
            name: status.name,
            status: status.status,
            progress: status.progress,
            error: status.error,
          });

          newSessions.set(sessionId, {
            ...session,
            attachmentStatuses: newStatuses,
          });

          return { sessions: newSessions };
        });
      },

      // ============================================
      // User Preferences
      // ============================================

      setRenderingMode: (mode: RenderingMode) => {
        set({ renderingMode: mode });
      },

      setTerminalPaneVisible: (visible: boolean) => {
        set({ terminalPaneVisible: visible });
      },

      setStructuredPanelVisible: (visible: boolean) => {
        set({ structuredPanelVisible: visible });
      },

      setSplitRatio: (ratio: number) => {
        set({ splitRatio: Math.max(0, Math.min(1, ratio)) });
      },

      // ============================================
      // Getters
      // ============================================

      getSession: (sessionId: string) => {
        return get().sessions.get(sessionId);
      },

      getActiveToolCount: (sessionId: string) => {
        const session = get().sessions.get(sessionId);
        return session?.activeTools.size || 0;
      },

      getActiveAgentCount: (sessionId: string) => {
        const session = get().sessions.get(sessionId);
        return session?.activeAgents.size || 0;
      },

      getPendingPermissionCount: (sessionId: string) => {
        const session = get().sessions.get(sessionId);
        return session?.pendingPermissions.size || 0;
      },

      getSessionState: (sessionId: string) => {
        const session = get().sessions.get(sessionId);
        return session?.currentState;
      },

      getTerminalContent: (sessionId: string) => {
        const session = get().sessions.get(sessionId);
        return session?.accumulatedContent || '';
      },
    })),
    {
      name: 'hybrid-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// ============================================
// Selectors
// ============================================

/**
 * Select session hybrid state
 */
export const useSessionHybridState = (sessionId: string) =>
  useHybridStore(useShallow((state) => state.sessions.get(sessionId)));

/**
 * Select current session state
 */
export const useSessionState = (sessionId: string) =>
  useHybridStore((state) => state.sessions.get(sessionId)?.currentState);

/**
 * Select terminal content
 */
export const useTerminalContent = (sessionId: string) =>
  useHybridStore((state) => state.sessions.get(sessionId)?.accumulatedContent || '');

/**
 * Select active tools
 */
export const useActiveTools = (sessionId: string) =>
  useHybridStore(useShallow((state) =>
    Array.from(state.sessions.get(sessionId)?.activeTools.values() || [])
  ));

/**
 * Select active agents
 */
export const useActiveAgents = (sessionId: string) =>
  useHybridStore(useShallow((state) =>
    Array.from(state.sessions.get(sessionId)?.activeAgents.values() || [])
  ));

/**
 * Select pending permissions
 */
export const usePendingPermissions = (sessionId: string) =>
  useHybridStore(useShallow((state) =>
    Array.from(state.sessions.get(sessionId)?.pendingPermissions.values() || [])
  ));

/**
 * Select rendering preferences
 */
export const useRenderingPreferences = () =>
  useHybridStore(useShallow((state) => ({
    mode: state.renderingMode,
    terminalVisible: state.terminalPaneVisible,
    structuredVisible: state.structuredPanelVisible,
    splitRatio: state.splitRatio,
  })));
